import { inferProcedureInput } from "@trpc/server";
import classNames from "classnames";
import { string } from "prop-types";
import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { common_utils } from "../../../../../../common/common_utils";
import { Constants } from "../../../../../../common/constants";
import { GuildData } from "../../../../../../common/sesh_types";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { DashboardUtils } from "../../../dashboard_utils";
import { GuildListContext } from "../../../sesh_router";
import { trpc } from "../../../utils/trpc";
import { ModifySettingsButton } from "../base_components/modify_settings_button";
import { InfoTooltip } from "../tooltips/info_tooltip";
import { WithTooltipPortaled } from "../tooltips/with_tooltip_portaled";

type ActivatedGuilds =
	RouterInferenceHelpers["premium"]["getUserPremiumDetails"]["output"]["activated_guilds"];

type SeshProGuilds =
	RouterInferenceHelpers["premium"]["getUserPremiumDetails"]["output"]["sesh_pro_guilds"];

export function PremiumServerListingsModal(props: {
	premium_details: RouterInferenceHelpers["premium"]["getUserPremiumDetails"]["output"];
	onClose: () => void;
}) {
	const subtitle = (
		<div className="flex items-center justify-center justify-items-center gap-2">
			<div className="flex items-center gap-2">
				Premium: {props.premium_details.transferrable_activated_servers_count}/
				{props.premium_details.max_activated_servers} activations
				<InfoTooltip
					info={
						<>
							You can activate servers here or by running the{" "}
							<span className="command-example">/premium</span>{" "}
							command in a server
						</>
					}
				/>
			</div>
		</div>
	);

	return (
		<DashboardUtils.SeshModal
			is_active={true}
			onClose={props.onClose}
			title="Manage Premium Servers"
			subtitle={subtitle}
			content={
				<PremiumServersListingContent
					activated_guilds={props.premium_details.activated_guilds}
					sesh_pro_guilds={props.premium_details.sesh_pro_guilds}
					show_sesh_pro={false}
				/>
			}
		/>
	);
}

export function SeshProServerListingsModal(props: {
	premium_details: RouterInferenceHelpers["premium"]["getUserPremiumDetails"]["output"];
	onClose: () => void;
}) {
	const subtitle = (
		<div className="flex items-center justify-center justify-items-center gap-2">
			<div className="flex items-center gap-2">
				sesh Pro: {props.premium_details.sesh_pro_activated_servers_count}/
				{props.premium_details.max_sesh_pro_activations} activations
				<InfoTooltip
					info={
						<>
							You can activate servers here or by running the{" "}
							<span className="command-example">/premium</span>{" "}
							command in a server. sesh Pro activations can be transferred between servers.
						</>
					}
				/>
			</div>
		</div>
	);

	return (
		<DashboardUtils.SeshModal
			is_active={true}
			onClose={props.onClose}
			title="Manage sesh Pro Servers"
			subtitle={subtitle}
			content={
				<SeshProServersListingContent
					activated_guilds={props.premium_details.activated_guilds}
					sesh_pro_guilds={props.premium_details.sesh_pro_guilds}
				/>
			}
		/>
	);
}

function PremiumServersListingContent(props: {
	activated_guilds: ActivatedGuilds;
	sesh_pro_guilds: SeshProGuilds;
	show_sesh_pro: boolean;
}) {
	const guild_list = useContext(GuildListContext);

	const sesh_pro_server_ids = props.sesh_pro_guilds.map((val) => val.id);
	const modifiable_server_ids = props.activated_guilds.map((val) => val.id);
	const all_activated_ids = [...sesh_pro_server_ids, ...modifiable_server_ids];
	const filtered_guild_list = guild_list.filter(
		(val) => !all_activated_ids.includes(val.id) && val.has_sesh
	);

	// TODO: get the icon URL so we can display proper image
	return (
		<div className="mt-4 flex h-fit max-h-[90%] min-h-0 overflow-auto">
			<ul className="flex min-h-0 w-full flex-col gap-2 overflow-auto">
				{props.show_sesh_pro && props.sesh_pro_guilds.map((val) => (
					<ServerListing
						key={val.id}
						{...val}
						icon={
							guild_list.find((data) => data.id === val.id)?.icon
						}
						is_deactivatable={false}
						is_discord_entitlement={false}
						is_sesh_pro
					/>
				))}
				{props.activated_guilds.map((val) => (
					<ServerListing
						key={val.id}
						{...val}
						icon={
							guild_list.find((data) => data.id === val.id)?.icon
						}
						is_deactivatable
					/>
				))}
				{filtered_guild_list.map((val) => (
					<ServerListing
						key={val.id}
						{...val}
						is_deactivatable={false}
						is_discord_entitlement={false}
					/>
				))}
			</ul>
		</div>
	);
}

function SeshProServersListingContent(props: {
	activated_guilds: ActivatedGuilds;
	sesh_pro_guilds: SeshProGuilds;
}) {
	const guild_list = useContext(GuildListContext);

	const sesh_pro_server_ids = props.sesh_pro_guilds.map((val) => val.id);
	const premium_server_ids = props.activated_guilds.map((val) => val.id);
	const all_activated_ids = [...sesh_pro_server_ids, ...premium_server_ids];
	const filtered_guild_list = guild_list.filter(
		(val) => !all_activated_ids.includes(val.id) && val.has_sesh
	);

	return (
		<div className="mt-4 flex h-fit max-h-[90%] min-h-0 overflow-auto">
			<ul className="flex min-h-0 w-full flex-col gap-2 overflow-auto">
				{props.sesh_pro_guilds.map((val) => (
					<SeshProServerListing
						key={val.id}
						{...val}
						icon={
							guild_list.find((data) => data.id === val.id)?.icon
						}
					/>
				))}
				{/* Show Premium-activated servers so users can still navigate to them */}
				{props.activated_guilds.map((val) => (
					<ServerListing
						key={val.id}
						{...val}
						icon={
							guild_list.find((data) => data.id === val.id)?.icon
						}
						is_deactivatable={false}
						is_discord_entitlement={val.is_discord_entitlement}
						is_premium_in_pro_list
					/>
				))}
				{filtered_guild_list.map((val) => (
					<ServerListing
						key={val.id}
						{...val}
						is_deactivatable={false}
						is_discord_entitlement={false}
						is_sesh_pro_activate
					/>
				))}
			</ul>
		</div>
	);
}

function ServerListing(props: {
	name: string;
	id: string;
	icon?: string;
	is_deactivatable: boolean;
	is_discord_entitlement: boolean;
	is_sesh_pro?: boolean;
	is_sesh_pro_activate?: boolean;
	is_premium_in_pro_list?: boolean;
}) {
	const icon = DashboardUtils.GetGuildIconURI(
		{ id: props.id, icon: props.icon },
		64
	);

	let cta: React.ReactNode;
	if (props.is_sesh_pro) {
		cta = <SeshProBadge />;
	} else if (props.is_premium_in_pro_list) {
		cta = (
			<Link to={common_utils.getGuildSettingsURL(props.id, "premium")}>
				<div className="flex items-center gap-2">
					<span className="rounded-md bg-sesh-primary px-2 py-1 text-sm font-semibold text-base-dark">
						Premium
					</span>
					<ModifySettingsButton>View</ModifySettingsButton>
				</div>
			</Link>
		);
	} else if (props.is_deactivatable) {
		cta = <SeverListingPremiumButton {...props} />;
	} else if (props.is_sesh_pro_activate) {
		cta = (
			<Link to={common_utils.getGuildSettingsURL(props.id, "premium")}>
				<ModifySettingsButton>Activate sesh Pro</ModifySettingsButton>
			</Link>
		);
	} else {
		cta = (
			<Link to={common_utils.getGuildSettingsURL(props.id, "premium")}>
				<ModifySettingsButton>Activate</ModifySettingsButton>
			</Link>
		);
	}

	return (
		<li className="flex items-center gap-2 rounded-sm bg-base-light shadow-sm">
			<div className="h-full w-[5rem] rounded-l-sm bg-base-dark p-2">
				<figure className="image flex h-full w-full items-center">
					<img className="is-rounded h-full w-full" src={icon}></img>
				</figure>
			</div>
			<div className="flex w-full flex-col items-center gap-2 p-2 sm:flex-row">
				<div className="flex flex-col">
					<div className="text-center text-base sm:text-left sm:text-lg">
						<span className="font-bold">{props.name}</span>
					</div>
					<div className="text-xs">
						<span className="font-mono">{props.id}</span>
					</div>
				</div>
				<div className="sm:ml-auto">{cta}</div>
			</div>
		</li>
	);
}

function SeshProServerListing(props: {
	name: string;
	id: string;
	icon?: string;
}) {
	const [is_loading, setIsLoading] = useState(false);

	const mutation = trpc.premium.modifyPremiumStatus.useMutation();
	const context = trpc.useContext();

	const icon = DashboardUtils.GetGuildIconURI(
		{ id: props.id, icon: props.icon },
		64
	);

	return (
		<li className="flex items-center gap-2 rounded-sm bg-base-light shadow-sm">
			<div className="h-full w-[5rem] rounded-l-sm bg-base-dark p-2">
				<figure className="image flex h-full w-full items-center">
					<img className="is-rounded h-full w-full" src={icon}></img>
				</figure>
			</div>
			<div className="flex w-full flex-col items-center gap-2 p-2 sm:flex-row">
				<div className="flex flex-col">
					<div className="text-center text-base sm:text-left sm:text-lg">
						<span className="font-bold">{props.name}</span>
					</div>
					<div className="text-xs">
						<span className="font-mono">{props.id}</span>
					</div>
				</div>
				<div className="sm:ml-auto">
					<div className="flex items-center gap-2">
						<span className="rounded-md bg-sesh-accent px-2 py-1 text-sm font-semibold text-white">
							sesh Pro
						</span>
						<button
							className={classNames(
								"button dashboard-warning-button w-fit",
								{ "is-loading": is_loading }
							)}
							onClick={() => {
								setIsLoading(true);
								mutation.mutate(
									{
										guild_id: props.id,
										modification: "Deactivate",
										activation_type: "sesh_pro",
									},
									{
										onSuccess: () => {
											setIsLoading(false);
											context.premium.getUserPremiumDetails.invalidate();
										},
										onError: () => {
											setIsLoading(false);
										},
									}
								);
							}}
						>
							Deactivate
						</button>
					</div>
				</div>
			</div>
		</li>
	);
}

function SeverListingPremiumButton(props: ActivatedGuilds[number]) {
	const [is_loading, setIsLoading] = useState(false);

	const mutation = trpc.premium.modifyPremiumStatus.useMutation();

	const context = trpc.useContext();

	if (props.is_discord_entitlement) {
		return (
			<div className="flex flex-col">
				<div>Discord Subscription</div>
				<div className="ml-auto">
					<a
						className="manual-link"
						target="_blank"
						href={Constants.DISCORD_PREMIUM_MANAGEMENT_FAQ_LINK}
					>
						Manage <i className="fas fa-external-link-alt" />
					</a>
				</div>
			</div>
		);
	}

	return (
		<button
			className={classNames(
				"button dashboard-warning-button ml-auto w-fit",
				{ "is-loading": is_loading }
			)}
			onClick={() => {
				setIsLoading(true);
				mutation.mutate(
					{
						guild_id: props.id,
						modification: "Deactivate",
					},
					{
						onSuccess: () => {
							setIsLoading(false);
							context.premium.getUserPremiumDetails.invalidate();
						},
					}
				);
			}}
		>
			Deactivate
		</button>
	);
}

function SeshProBadge() {
	return (
		<div className="flex items-center gap-2">
			<span className="rounded-md bg-sesh-accent px-2 py-1 text-sm font-semibold text-white">
				sesh Pro
			</span>
			<WithTooltipPortaled
				tooltip={
					<>
						This server has sesh Pro activated. Manage sesh Pro
						activations from the sesh Pro section above.
					</>
				}
				style="Info"
			>
				<i className="fa fa-info-circle fa-xs"></i>
			</WithTooltipPortaled>
		</div>
	);
}
